import { useCallback, useEffect, useRef, useState } from "react";
import API_ROUTES_V2 from "~/api-routes-v2";
import apiService from "~/services/service";

const PAGE_SIZE = 20;

export interface DocumentAuditItem {
  id: string;
  action: "create" | "update" | "delete";
  actor_name: string;
  actor_email: string;
  name: string;
  old_data?: Record<string, unknown>;
  new_data?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  timestamp?: string;
  attachment?: string;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatDisplayNamePeriod(name: string): string {
  if (!name) return name;
  return name.replace(/\b(\d{2})\/(\d{4})\b/, (_, mm, yyyy) => {
    const monthNum = parseInt(mm, 10);
    if (monthNum >= 1 && monthNum <= 12) {
      return `${MONTHS[monthNum - 1]} ${yyyy}`;
    }
    return _;
  });
}

function safeDecode(value: unknown): string {
  if (value == null) return "";
  const str = String(value);
  try {
    return decodeURIComponent(str);
  } catch {
    return str;
  }
}

function flattenAuditItem(item: any): DocumentAuditItem {
  const d = item?.dataValues ?? item;

  const decodeData = (
    data: Record<string, unknown> | unknown[] | null | undefined,
  ): Record<string, unknown> => {
    if (!data || Array.isArray(data)) return {};
    return Object.fromEntries(
      Object.entries(data).map(([k, v]) => [
        k,
        typeof v === "string" ? safeDecode(v) : v,
      ]),
    );
  };

  return {
    id: d.id,
    action: d.action,
    actor_name: d.actor_name,
    actor_email: d.actor_email,
    name: formatDisplayNamePeriod(item.name ?? d.name ?? ""),
    attachment: d.attachment ?? "",
    old_data: decodeData(d.old_data),
    new_data: decodeData(d.new_data),
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    timestamp: d.createdAt,
  };
}

function normalizeAuditResponse(
  response: unknown,
): DocumentAuditItem[] | undefined {
  const toItems = (arr: unknown[]) => arr.map(flattenAuditItem);
  if (Array.isArray(response)) return toItems(response);
  const data = (response as any)?.data;
  if (Array.isArray(data)) return toItems(data);
  const nested = (response as any)?.data?.data;
  if (Array.isArray(nested)) return toItems(nested);
  return undefined;
}

export type UseDocumentHistoryArgs = {
  attachmentId: string | undefined;
  blobFilter?: string | undefined;
  objectType?: string;
  enabled?: boolean;
};

export type UseDocumentHistoryResult = {
  items: DocumentAuditItem[];
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  isEmpty: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

export function useDocumentHistory({
  attachmentId,
  blobFilter,
  objectType = "ENTITIES_PLUS",
  enabled = true,
}: UseDocumentHistoryArgs): UseDocumentHistoryResult {
  const [items, setItems] = useState<DocumentAuditItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const pageRef = useRef(1);

  const fetchPage = useCallback(
    async (pageNum: number, append: boolean) => {
      if (!attachmentId) return;
      if (pageNum === 1) setIsLoading(true);
      else setIsFetchingMore(true);
      setError(null);
      try {
        const response = await apiService(
          API_ROUTES_V2.FETCH_ATTACHMENT_AUDIT,
          "GET",
          {
            replace: {
              id: attachmentId,
              page: String(pageNum),
              objectType,
              includeUpdates: "true",
            },
            skipCache: true,
          },
        );

        let list = normalizeAuditResponse(response) ?? [];
        if (blobFilter?.trim()) {
          const filter = blobFilter.trim();
          list = list.filter(
            (item) =>
              item.attachment === filter ||
              item.attachment?.endsWith(filter) ||
              item.attachment?.includes(filter) ||
              item.name === filter,
          );
        }
        if (list.length < PAGE_SIZE) setHasMore(false);
        if (append) {
          setItems((prev) => {
            const existingIds = new Set(prev.map((i) => i.id));

            return [...prev, ...list.filter((i) => !existingIds.has(i.id))];
          });
        } else {
          setItems(list);
        }
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
        if (!append) setItems([]);
        setHasMore(false);
      } finally {
        setIsLoading(false);
        setIsFetchingMore(false);
      }
    },
    [attachmentId, blobFilter, objectType],
  );

  const refetch = useCallback(async () => {
    pageRef.current = 1;
    setHasMore(true);
    await fetchPage(1, false);
  }, [fetchPage]);

  useEffect(() => {
    if (!enabled || !attachmentId) {
      setItems([]);
      pageRef.current = 1;
      setHasMore(true);
      return;
    }
    pageRef.current = 1;
    setHasMore(true);
    fetchPage(1, false);
  }, [enabled, attachmentId, blobFilter, objectType]);

  const loadMore = useCallback(() => {
    if (!attachmentId || !hasMore || isLoading || isFetchingMore) return;
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;
    fetchPage(nextPage, true);
  }, [attachmentId, hasMore, isLoading, isFetchingMore, fetchPage]);

  return {
    items,
    isLoading,
    isFetchingMore,
    hasMore,
    loadMore,
    isEmpty: !isLoading && items.length === 0,
    error,
    refetch,
  };
}
